import { type ReactNode } from 'react'
import { Link } from 'react-router'
import { useSubscription } from '@/hooks/useSubscription'
import { useLabels } from '@/lib/terminology'

type UsageType = 'recording' | 'guest' | 'series' | 'episode'

interface UsageGateProps {
  type: UsageType
  /** Required for 'episode' type to check per-series limit */
  seriesId?: string
  children: ReactNode
  /** Render prop for custom blocked UI */
  renderBlocked?: (reason: 'limit' | 'expired') => ReactNode
}

/**
 * Wraps UI elements that require subscription access.
 * Shows upgrade prompt if user is over limits or trial expired.
 *
 * @example
 * <UsageGate type="recording">
 *   <CreateRecordingButton />
 * </UsageGate>
 *
 * @example
 * <UsageGate type="series">
 *   <CreateSeriesButton />
 * </UsageGate>
 *
 * @example
 * <UsageGate type="episode" seriesId={seriesId}>
 *   <CreateEpisodeButton />
 * </UsageGate>
 */
export function UsageGate({ type, seriesId: _seriesId, children, renderBlocked }: UsageGateProps) {
  const {
    isReadOnly,
    canCreateRecording,
    canAddGuest,
    canCreateSeries,
  } = useSubscription()

  // For 'episode' type, we'd ideally check per-series limits via useCanAddEpisodeToSeries
  // but for simplicity in the gate, we rely on canCreateRecording as a proxy
  // The actual per-series check happens in the episode creation flow
  let canProceed: boolean
  switch (type) {
    case 'recording':
      canProceed = canCreateRecording
      break
    case 'guest':
      canProceed = canAddGuest
      break
    case 'series':
      canProceed = canCreateSeries
      break
    case 'episode':
      // Episode limit is per-series, checked elsewhere
      // Here we just check if user can create at all (not read-only)
      canProceed = !isReadOnly && canCreateRecording
      break
    default:
      canProceed = true
  }

  // Determine block reason
  const blockReason = isReadOnly ? 'expired' : 'limit'

  if (!canProceed) {
    if (renderBlocked) {
      return <>{renderBlocked(blockReason)}</>
    }

    const limitLabel = type === 'episode' ? 'episodes per series' : type

    return (
      <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg p-4 bg-zinc-50 dark:bg-zinc-800/50">
        <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-2">
          {blockReason === 'expired'
            ? 'Your trial has ended.'
            : `You've reached your ${limitLabel} limit.`}
        </p>
        <Link
          to="/pricing"
          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
        >
          Upgrade to continue &rarr;
        </Link>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * Hook version of UsageGate for conditional logic in components
 */
export function useCanCreate(type: 'recording' | 'guest' | 'series'): {
  allowed: boolean
  remaining: number
  reason: 'ok' | 'limit' | 'expired'
} {
  const {
    isReadOnly,
    canCreateRecording,
    canAddGuest,
    canCreateSeries,
    recordingsRemaining,
    guestsRemaining,
    seriesRemaining,
  } = useSubscription()

  let allowed: boolean
  let remaining: number

  switch (type) {
    case 'recording':
      allowed = canCreateRecording
      remaining = recordingsRemaining
      break
    case 'guest':
      allowed = canAddGuest
      remaining = guestsRemaining
      break
    case 'series':
      allowed = canCreateSeries
      remaining = seriesRemaining
      break
    default:
      allowed = true
      remaining = 0
  }

  let reason: 'ok' | 'limit' | 'expired' = 'ok'
  if (!allowed) {
    reason = isReadOnly ? 'expired' : 'limit'
  }

  return { allowed, remaining, reason }
}

/**
 * Display remaining usage with progress bar
 */
export function UsageIndicator({ type }: { type: 'recording' | 'guest' | 'series' }) {
  const { recordingsRemaining, guestsRemaining, seriesRemaining, subscription } =
    useSubscription()
  const labels = useLabels()

  let remaining: number
  let limit: number | undefined
  let label: string

  switch (type) {
    case 'recording':
      remaining = recordingsRemaining
      limit = subscription?.recordings_limit
      label = labels.recordings
      break
    case 'guest':
      remaining = guestsRemaining
      limit = subscription?.guests_limit
      label = labels.guests
      break
    case 'series':
      remaining = seriesRemaining
      limit = subscription?.series_limit
      label = labels.collections
      break
    default:
      remaining = 0
      limit = 0
      label = ''
  }

  if (!subscription) return null

  const percentage = limit ? ((limit - remaining) / limit) * 100 : 0
  const isLow = remaining <= 1

  return (
    <div className="text-sm">
      <div className="flex justify-between mb-1">
        <span className="text-zinc-600 dark:text-zinc-400">{label}</span>
        <span
          className={
            isLow
              ? 'text-amber-600 dark:text-amber-400 font-medium'
              : 'text-zinc-600 dark:text-zinc-400'
          }
        >
          {remaining} remaining
        </span>
      </div>
      <div className="h-2 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all ${
            isLow ? 'bg-amber-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}
